// src/components/CreateList.js - Enhanced version with image upload (Fixed)
import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, AlertTriangle, Upload, Image as ImageIcon } from 'lucide-react';
import { createNewList, uploadImage, uploadImageWithRetry} from '../utils/api';
import { generateFingerprint } from '../utils/fingerprint';
import Footer from "./Footer"

const CreateList = ({ creatorId }) => {
  const [listTitle, setListTitle] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [items, setItems] = useState([{ name: '', imageUrl: '', imageFile: null, uploading: false }]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const bottomRef = useRef(null);
  const fileInputRefs = useRef([]);

  // Calculate duplicates
  const duplicates = useMemo(() => {
    const nameCount = {};
    const imageUrlCount = {};
    const result = { names: new Set(), imageUrls: new Set() };

    items.forEach((item, index) => {
      // Check for duplicate names (case-insensitive, ignore empty)
      if (item.name.trim()) {
        const normalizedName = item.name.trim().toLowerCase();
        if (!nameCount[normalizedName]) {
          nameCount[normalizedName] = [];
        }
        nameCount[normalizedName].push(index);
      }

      // Check for duplicate image URLs (ignore empty)
      if (item.imageUrl.trim()) {
        const normalizedUrl = item.imageUrl.trim();
        if (!imageUrlCount[normalizedUrl]) {
          imageUrlCount[normalizedUrl] = [];
        }
        imageUrlCount[normalizedUrl].push(index);
      }
    });

    // Mark indices with duplicates
    Object.values(nameCount).forEach(indices => {
      if (indices.length > 1) {
        indices.forEach(index => result.names.add(index));
      }
    });

    Object.values(imageUrlCount).forEach(indices => {
      if (indices.length > 1) {
        indices.forEach(index => result.imageUrls.add(index));
      }
    });

    return result;
  }, [items]);

  const addItem = () => {
    if (items.length >= 500) {
      alert('You cannot add more than 500 items.');
      return;
    }

    setItems([...items, { name: '', imageUrl: '', imageFile: null, uploading: false }]);
    
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
      // Remove the corresponding file input ref
      fileInputRefs.current = fileInputRefs.current.filter((_, i) => i !== index);
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  // Updated handleImageUpload function for CreateList component
  const handleImageUpload = async (index, file) => {
    if (!file) return;

    try {
      // Update item to show uploading state
      updateItem(index, 'uploading', true);
      
      console.log('Starting upload for file:', file.name, 'Size:', file.size, 'Type:', file.type);
      
      // Use the retry version of uploadImage with progress tracking
      const result = await uploadImageWithRetry(file, 3, (progress) => {
        // Optional: you could add a progress indicator here
        console.log(`Upload progress: ${Math.round(progress)}%`);
      });
      
      console.log("Upload result:", result);
      
      if (result.success && result.imageUrl) {
        // Update the item with the uploaded image URL
        updateItem(index, 'imageUrl', result.imageUrl);
        updateItem(index, 'imageFile', file);
        console.log('Upload successful, URL:', result.imageUrl);
      } else {
        throw new Error('Upload failed - no image URL returned');
      }
      
    } catch (error) {
      console.error('Error uploading image:', error);
      
      // Provide user-friendly error messages
      let userMessage = 'Failed to upload image. ';
      
      if (error.message.includes('too large')) {
        userMessage += 'The file is too large. Please choose an image smaller than 5MB.';
      } else if (error.message.includes('Only image files')) {
        userMessage += 'Please select an image file (JPG, PNG, GIF, etc.).';
      } else if (error.message.includes('Network error') || error.message.includes('timed out')) {
        userMessage += 'Network error. Please check your connection and try again.';
      } else if (error.message.includes('incomplete') || error.message.includes('corrupted')) {
        userMessage += 'The upload was incomplete. Please try again.';
      } else {
        userMessage += 'Please try again in a moment.';
      }
      
      alert(userMessage);
      
      // Clear the file input on error
      if (fileInputRefs.current[index]) {
        fileInputRefs.current[index].value = '';
      }
      
    } finally {
      // Remove uploading state
      updateItem(index, 'uploading', false);
    }
  };

  const handleFileSelect = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageUpload(index, file);
    }
  };

  const removeImage = (index) => {
    updateItem(index, 'imageUrl', '');
    updateItem(index, 'imageFile', null);
    // Reset the file input
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!listTitle.trim()) return;

    const validItems = items.filter(item => item.name.trim());
    if (validItems.length < 2) {
      alert('Please add at least 2 items to your list.');
      return;
    }

    // Check if any images are still uploading
    const uploadingItems = items.filter(item => item.uploading);
    if (uploadingItems.length > 0) {
      alert('Please wait for all images to finish uploading before creating the list.');
      return;
    }

    // Check for duplicates and ask for confirmation
    if (duplicates.names.size > 0 || duplicates.imageUrls.size > 0) {
      const duplicateTypes = [];
      if (duplicates.names.size > 0) duplicateTypes.push('item names');
      if (duplicates.imageUrls.size > 0) duplicateTypes.push('image URLs');
      
      const confirmed = window.confirm(
        `Warning: You have duplicate ${duplicateTypes.join(' and ')} in your list. Are you sure you want to continue creating the list?`
      );
      if (!confirmed) {
        return;
      }
    }

    setIsLoading(true);
    try {
      const listItems = validItems.map(item => ({
        name: item.name.trim(),
        imageUrl: item.imageUrl.trim() || '',
        rating: 1200,
        wins: 0,
        losses: 0
      }));
      
      const finger = generateFingerprint();
      const result = await createNewList(listTitle.trim(), listDescription.trim(), listItems, finger);

      console.log('List created successfully:', result);
      navigate(`/list/${result.listID}`);
    } catch (error) {
      console.error('Error creating list:', error);
      alert(error.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <button onClick={() => navigate('/')} className="back-btn">
        <ArrowLeft size={20} />
        Back to Home
      </button>

      <button onClick={() => navigate('/imageRules')} className="create-btn">
        Rules
      </button>

      <div className="card">
        <h1 className="title">Create New List</h1>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="listTitle">List Title</label>
            <input
              id="listTitle"
              type="text"
              placeholder="Enter list title"
              value={listTitle}
              onChange={(e) => setListTitle(e.target.value)}
              className="input"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="listDescription">Description (optional)</label>
            <textarea
              id="listDescription"
              placeholder="Describe your list (optional)"
              value={listDescription}
              onChange={(e) => setListDescription(e.target.value)}
              className="input"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Items</label>
            {items.map((item, index) => (
              <div key={index} className="item-group item-group-container">
                <div className="inputs-and-image-container">
                  {/* Left side: Input fields */}
                  <div className="inputs-container">
                    {/* Name Input */}
                    <div className="name-input-wrapper">
                      <input
                        type="text"
                        placeholder="Item name"
                        value={item.name}
                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                        className={`input ${duplicates.names.has(index) ? 'input-error' : ''}`}
                        required
                      />
                      {duplicates.names.has(index) && (
                        <div className="duplicate-warning">
                          <AlertTriangle size={16} />
                          Warning: Duplicate name
                        </div>
                      )}
                    </div>

                    {/* URL Input */}
                    <div className="url-input-wrapper">
                      <input
                        type="url"
                        placeholder="Or enter image URL"
                        value={item.imageUrl}
                        onChange={(e) => updateItem(index, 'imageUrl', e.target.value)}
                        className={`input ${duplicates.imageUrls.has(index) ? 'input-error' : ''}`}
                      />
                      {duplicates.imageUrls.has(index) && (
                        <div className="duplicate-warning">
                          <AlertTriangle size={16} />
                          Warning: Duplicate image URL
                        </div>
                      )}
                      <div className="url-input-help-text">
                        Upload an image or paste a URL here
                      </div>
                    </div>
                  </div>

                  {/* Right side: Image upload/preview */}
                  <div className="image-container">
                    <div className="image-upload-area">
                      {item.uploading ? (
                        <div className="upload-spinner-container">
                          <div className="upload-spinner" />
                          <span className="upload-spinner-text">Uploading...</span>
                        </div>
                      ) : item.imageUrl ? (
                        <div className="image-preview-container">
                          <img 
                            key={item.imageUrl} // Force re-render when URL changes
                            src={item.imageUrl} 
                            alt={item.name || 'Item image'}
                            className="image-preview"
                            onLoad={(e) => {
                              // Reset display styles on successful load
                              e.target.style.display = 'block';
                              if (e.target.nextSibling) {
                                e.target.nextSibling.style.display = 'none';
                              }
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) {
                                e.target.nextSibling.style.display = 'flex';
                              }
                            }}
                          />
                          <div className="image-error-fallback" style={{ display: 'none' }}>
                            Failed to load image
                          </div>
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="image-remove-button"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="image-upload-placeholder" onClick={() => fileInputRefs.current[index]?.click()}>
                          <ImageIcon size={32} color="#a0aec0" className="image-upload-icon" />
                          <div className="image-upload-text">Click to upload</div>
                        </div>
                      )}
                      
                      <input
                        ref={el => fileInputRefs.current[index] = el}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileSelect(index, e)}
                        className="hidden-file-input"
                      />
                    </div>
                  </div>

                  {/* Remove button */}
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="btn btn-danger-small"
                      style={{ alignSelf: 'flex-start' }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            <button type="button" onClick={addItem} className="btn btn-outline">
              <Plus size={16} />
              Add Item
            </button>
            <div ref={bottomRef} />
          </div>

          <button type="submit" disabled={isLoading} className="btn btn-primary">
            {isLoading ? 'Creating...' : 'Create List'}
          </button>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default CreateList;